import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import JSZip from "jszip";
import { useHistory, useLocation } from "react-router-dom";

import {
  Grid,
  Button,
  IconButton,
  List,
  ListItem,
  Input,
  InputLabel,
  CircularProgress,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import FloatingPanel from "./FloatingPanel";

import { GeofencesContext } from "../../context";
import { GeofencesPanelPositions } from "../../constants";
import {
  guid,
  parseGeoJSONFile,
  parseKMLFile,
  getGeofencesSavedData,
  removeGeofencesSavedData,
} from "../../services/geofences.service";
import { useDialog } from "../../hooks/useDialog";

const useStyles = makeStyles((theme) => ({
  textButton: {
    marginBottom: theme.spacing(2),
  },
  wipTitle: { marginBottom: theme.spacing(1) },
  wipItem: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  wipItemTitle: {
    marginRight: theme.spacing(2),
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
}));

const StartingMenuPanel = ({ fleetId, sidewalksMode }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { t } = useTranslation();
  const { initGeofencesData, fetchFleetGeofences, setError } = useContext(GeofencesContext);
  const history = useHistory();
  const location = useLocation();

  const savedData = getGeofencesSavedData({ fleetId });
  const filteredSavedData = Object.keys(savedData)
    // support old data where sidewalks propety is undefined
    .filter((key) => (sidewalksMode ? savedData[key].sidewalks : !savedData[key].sidewalks))
    .reduce((res, key) => Object.assign(res, { [key]: savedData[key] }), {});

  const [loading, setLoading] = useState(false);
  const [workInProgress, setWorkInProgress] = useState(filteredSavedData);

  const [openDeleteDialog, deleteDialog] = useDialog({
    title: t("Fleet.Geofences.Dialogs.DeleteWIPTitle"),
    body: t("Fleet.Geofences.Dialogs.DeleteWIPBody"),
  });

  const onEditFleetButtonClick = async () => {
    if (sidewalksMode) {
      setLoading(true);
      try {
        await fetchFleetGeofences(fleetId);
      } catch (_) {
        setLoading(false);
      }
    } else {
      history.push({
        pathname: `${location.pathname}/edit`,
      });
    }
  };

  const onSelectWIP = (key) => {
    const { name, layers } = getGeofencesSavedData({ key });

    initGeofencesData({ key, name, editable: true, layers });
  };

  const onDeleteWIP = (event, key) => {
    event.stopPropagation();

    openDeleteDialog(() => () => {
      const wip = { ...workInProgress };

      delete wip[key];
      setWorkInProgress(wip);
      removeGeofencesSavedData(key);
    });
  };

  const selectFile = async (event) => {
    const file = event.target.files[0];
    const fileParts = file.name.split(".");
    const name = fileParts.slice(0, -1).join(".");
    const extension = fileParts.pop();

    const reader = new FileReader();

    reader.onload = () => {
      try {
        let layers = {};

        if (extension === "geojson") {
          layers = parseGeoJSONFile(reader.result);
        } else if (extension === "kml" || extension === "kmz") {
          layers = parseKMLFile(reader.result);
        } else if (extension === "json") {
          layers = JSON.parse(reader.result).layers;
        }

        initGeofencesData({ key: guid(), name, editable: true, layers });
      } catch (_) {
        setError(t("Fleet.Geofences.ImportFileError"));
      }
    };

    if (extension === "kmz") {
      try {
        const zip = await JSZip.loadAsync(file);
        const files = Object.values(zip.files);

        if (files?.[0]) {
          const result = await files[0].async("text");
          const layers = parseKMLFile(result);

          initGeofencesData({ key: guid(), name, editable: true, layers });
        }
      } catch (_) {
        setError(t("Fleet.Geofences.ImportFileError"));
      }
    } else {
      reader.readAsText(file);
    }
  };

  return (
    <FloatingPanel position={GeofencesPanelPositions.LEFT}>
      <Grid>
        <Input
          type="file"
          id="uploadFile"
          data-testid="upload-file"
          inputProps={{ accept: ".geojson, .kml, .kmz, .json" }}
          onChange={selectFile}
          style={{ display: "none" }}
        />
        <InputLabel htmlFor="uploadFile">
          <Button
            fullWidth
            color="primary"
            variant="contained"
            component="span"
            className={classes.textButton}
          >
            {t("Fleet.Geofences.Buttons.Import")}
          </Button>
        </InputLabel>
        <Button
          fullWidth
          color="primary"
          variant="contained"
          disabled={loading}
          className={classes.textButton}
          onClick={onEditFleetButtonClick}
        >
          {loading ? (
            <CircularProgress size={24} color="secondary" />
          ) : (
            t(
              sidewalksMode
                ? "Fleet.Sidewalks.Buttons.EditFleet"
                : "Fleet.Geofences.Buttons.EditFleet",
            )
          )}
        </Button>
        {Object.values(workInProgress).length > 0 && (
          <Grid>
            <h3 className={classes.wipTitle}>{t("Fleet.Geofences.WIPTitle")}</h3>
            <List className={classes.wipList}>
              {Object.values(workInProgress).map((work) => (
                <ListItem key={work.key} button onClick={() => onSelectWIP(work.key)}>
                  <div className={classes.wipItem}>
                    <span className={classes.wipItemTitle}>{work.name}</span>
                    <IconButton size="small" onClick={(event) => onDeleteWIP(event, work.key)}>
                      <DeleteIcon />
                    </IconButton>
                  </div>
                </ListItem>
              ))}
            </List>
          </Grid>
        )}
        {deleteDialog}
      </Grid>
    </FloatingPanel>
  );
};

StartingMenuPanel.propTypes = {
  fleetId: PropTypes.string.isRequired,
  sidewalksMode: PropTypes.bool,
};

StartingMenuPanel.defaultProps = {
  sidewalksMode: false,
};

export default StartingMenuPanel;
